import { format } from "date-fns";
import isEmpty from "../../../../common/helpers/isEmpty";
import {
  ApprovalStateChange,
  EntryChange,
  LeaveAttachmentAction,
  LunchBreak,
  Supplement,
  WorkTimeEntryChangeDetails,
} from "../../entriesAPI";
import {
  FieldChange,
  ParsedApprovalChange,
  ParsedEntryChange,
} from "../../types";

export const parseEntryChanges = (
  entryChanges: EntryChange[],
  t: (key: string) => string,
  entryType: Record<string, string>,
): ParsedEntryChange[] => {
  if (isEmpty(entryChanges) || entryChanges.length <= 1) return [];

  const sorted = [...entryChanges].sort((a, b) => b.timestamp - a.timestamp);

  return sorted.reduce<ParsedEntryChange[]>((acc, current, index) => {
    if (index === sorted.length - 1) return acc;

    if (!current.details) return acc;

    // Handle attachment actions separately (they don't compare to previous state)
    if (current.details.leaveAttachmentAction) {
      const attachmentChange: FieldChange = {
        field: "leaveAttachment",
        displayName: t("hourEntries:entryChangeFields.leaveAttachment"),
        previousValue: "",
        newValue:
          current.details.leaveAttachmentAction ===
          LeaveAttachmentAction.UPLOADED
            ? t("hourEntries:attachmentUploaded")
            : t("hourEntries:attachmentDeleted"),
      };

      acc.push({
        timestamp: current.timestamp,
        userId: current.userId,
        userFirstName: current.userFirstName || "",
        userLastName: current.userLastName || "",
        userType: current.userType,
        fields: [attachmentChange],
      });

      return acc;
    }

    // Regular field comparison
    const previous = sorted[index + 1];
    if (!previous.details) return acc;

    const fields = compareDetails(
      current.details,
      previous.details,
      t,
      entryType,
    );

    if (!isEmpty(fields)) {
      acc.push({
        timestamp: current.timestamp,
        userId: current.userId,
        userFirstName: current.userFirstName || "",
        userLastName: current.userLastName || "",
        userType: current.userType,
        fields,
      });
    }

    return acc;
  }, []);
};

/**
 * Normalizes supplements to have consistent structure for comparison.
 * Backend sometimes returns supplements without null fields, sometimes with them.
 */
const normalizeSupplements = (
  supplements: Supplement[] | undefined,
): Supplement[] => {
  if (!supplements) return [];

  return supplements.map((s) => ({
    code: s.code,
    amount: s.amount ?? null,
    time: s.time ?? null,
    description: s.description ?? null,
  }));
};

/**
 * Normalizes lunch break to have consistent structure for comparison.
 * Backend sometimes returns without null fields, sometimes with them.
 */
const normalizeLunchBreak = (
  lunchBreak: LunchBreak | null | undefined,
): LunchBreak | null => {
  if (!lunchBreak) return null;

  return {
    start: lunchBreak.start,
    duration: lunchBreak.duration,
  };
};

const compareDetails = (
  current: WorkTimeEntryChangeDetails,
  previous: WorkTimeEntryChangeDetails,
  t: (key: string) => string,
  entryType: Record<string, string>,
): FieldChange[] => {
  const changes: FieldChange[] = [];

  if (current.start !== previous.start) {
    changes.push({
      field: "startTime",
      displayName: t("hourEntries:entryChangeFields.startTime"),
      previousValue: format(new Date(previous.start), "HH:mm"),
      newValue: format(new Date(current.start), "HH:mm"),
    });
  }

  if (current.end !== previous.end) {
    changes.push({
      field: "endTime",
      displayName: t("hourEntries:entryChangeFields.endTime"),
      previousValue: format(new Date(previous.end), "HH:mm"),
      newValue: format(new Date(current.end), "HH:mm"),
    });
  }

  const normalizedCurrentLunch = normalizeLunchBreak(current.lunchBreak);
  const normalizedPreviousLunch = normalizeLunchBreak(previous.lunchBreak);
  const lunchChanged =
    JSON.stringify(normalizedCurrentLunch) !==
    JSON.stringify(normalizedPreviousLunch);

  if (lunchChanged) {
    changes.push({
      field: "lunchBreak",
      displayName: t("hourEntries:entryChangeFields.lunchBreak"),
      previousValue: formatLunchBreak(previous.lunchBreak, t),
      newValue: formatLunchBreak(current.lunchBreak, t),
    });
  }

  const normalizedCurrentSupplements = normalizeSupplements(
    current.supplements,
  );
  const normalizedPreviousSupplements = normalizeSupplements(
    previous.supplements,
  );
  const supplementsChanged =
    JSON.stringify(normalizedCurrentSupplements) !==
    JSON.stringify(normalizedPreviousSupplements);

  if (supplementsChanged) {
    changes.push({
      field: "supplements",
      displayName: t("hourEntries:entryChangeFields.supplements"),
      previousValue: formatSupplements(previous.supplements, t),
      newValue: formatSupplements(current.supplements, t),
    });
  }

  if (current.customerCostCenterId !== previous.customerCostCenterId) {
    changes.push({
      field: "costCenter",
      displayName: t("hourEntries:entryChangeFields.costCenter"),
      previousValue: previous.customerCostCenterName,
      newValue: current.customerCostCenterName,
    });
  }

  // Entry type change (e.g. WORK_ENTRY -> SICK_LEAVE_WITH_CERT)
  if (current.type !== previous.type) {
    changes.push({
      field: "type",
      displayName: t("hourEntries:entryChangeFields.type"),
      previousValue: entryType[previous.type] || previous.type,
      newValue: entryType[current.type] || current.type,
    });
  }

  const normalizedCurrentMaxHours = current.maxHoursToBank || null;
  const normalizedPreviousMaxHours = previous.maxHoursToBank || null;

  if (normalizedCurrentMaxHours !== normalizedPreviousMaxHours) {
    changes.push({
      field: "maxHoursToBank",
      displayName: t("hourEntries:entryChangeFields.maxHoursToBank"),
      previousValue: previous.maxHoursToBank || t("common:noValue"),
      newValue: current.maxHoursToBank || t("common:noValue"),
    });
  }

  return changes;
};

const formatLunchBreak = (
  lunchBreak: LunchBreak | null,
  t: (key: string) => string,
): string => {
  if (!lunchBreak) return t("common:noValue");

  const start = format(new Date(lunchBreak.start), "HH:mm");
  const end = format(
    new Date(lunchBreak.start + lunchBreak.duration * 60 * 1000),
    "HH:mm",
  );
  return `${start} - ${end} (${lunchBreak.duration} min)`;
};

const formatSupplements = (
  supplements: Supplement[],
  t: (key: string) => string,
): string => {
  if (isEmpty(supplements)) return t("common:noValue");

  return supplements
    .map((s) => {
      const supplementName = t(`supplements:${s.code}`) || s.code;

      if (!s.description) {
        return supplementName;
      }

      return `${supplementName} (${s.description})`;
    })
    .join(", ");
};

export const parseApprovalChanges = (
  approvalChanges: ApprovalStateChange[],
): ParsedApprovalChange[] => {
  if (isEmpty(approvalChanges)) return [];

  return [...approvalChanges]
    .sort((a, b) => b.timestamp - a.timestamp)
    .map((change) => ({
      timestamp: change.timestamp,
      userId: change.userId,
      userFirstName: change.userFirstName,
      userLastName: change.userLastName,
      userType: change.userType,
      approvalState: change.approvalState,
      comment: change.comment,
    }));
};
